import React from 'react'
import {
    Typography,
    TextField,
    FormGroup,
    FormControlLabel,
    Checkbox,
    useTheme,
    Switch,
} from '@mui/material'
import { DatePicker } from '@material-ui/pickers'
import { EntityClass } from '../../../../models'
import { getEntityClassDisplayName } from '../../../../models/DocumentAudit/services'
import { FilterValue } from './types'

interface AuditFilterSectionProps {
    classification: EntityClass
    filter: FilterValue
    onFilterChange: (
        classification: EntityClass,
        updates: Partial<FilterValue>,
    ) => void
}

export const AuditFilterSection: React.FC<AuditFilterSectionProps> = ({
    classification,
    filter,
    onFilterChange,
}) => {
    const theme = useTheme()

    const handleFilterChange = (updates: Partial<FilterValue>) => {
        onFilterChange(classification, updates)
    }

    const handleClearFilter = () => {
        handleFilterChange({
            min: null,
            max: null,
            enabled: false,
        })
    }

    const hasActiveFilters =
        filter.min !== null || filter.max !== null || filter.enabled

    return (
        <div
            style={{
                marginBottom: theme.spacing(3),
                padding: theme.spacing(2),
                backgroundColor: theme.palette.background.paper,
                borderRadius: theme.shape.borderRadius,
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: theme.spacing(2),
                }}
            >
                <Typography
                    variant="h6"
                    style={{
                        color: filter.enabled
                            ? theme.palette.primary.main
                            : theme.palette.text.primary,
                        fontWeight: 500,
                    }}
                >
                    {getEntityClassDisplayName(classification.name)}
                </Typography>
                {hasActiveFilters && (
                    <button
                        onClick={handleClearFilter}
                        style={{
                            border: 'none',
                            background: 'none',
                            color: theme.palette.primary.main,
                            cursor: 'pointer',
                            padding: theme.spacing(1),
                            fontSize: '0.875rem',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        Clear
                    </button>
                )}
            </div>

            {classification.data_type === 'DATE' && (
                <div style={{ display: 'flex', gap: theme.spacing(2) }}>
                    <DatePicker
                        label="From"
                        value={filter.min || null}
                        onChange={(date) =>
                            handleFilterChange({ min: date?.toISOString() })
                        }
                    />
                    <DatePicker
                        label="To"
                        value={filter.max || null}
                        onChange={(date) =>
                            handleFilterChange({ max: date?.toISOString() })
                        }
                    />
                </div>
            )}

            {(classification.data_type === 'CURRENCY' ||
                classification.data_type === 'NUMBER') && (
                <div style={{ display: 'flex', gap: theme.spacing(2) }}>
                    <TextField
                        label="Min"
                        type="number"
                        value={filter.min || ''}
                        onChange={(e) =>
                            handleFilterChange({ min: e.target.value })
                        }
                    />
                    <TextField
                        label="Max"
                        type="number"
                        value={filter.max || ''}
                        onChange={(e) =>
                            handleFilterChange({ max: e.target.value })
                        }
                    />
                </div>
            )}

            {classification.data_type === 'TEXT' && (
                <TextField
                    label="Contains"
                    value={filter.min || ''}
                    onChange={(e) =>
                        handleFilterChange({ min: e.target.value })
                    }
                    fullWidth
                />
            )}

            {classification.data_type === 'BOOLEAN' && (
                <FormGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={Boolean(filter.min)}
                                onChange={(e) =>
                                    handleFilterChange({
                                        min: e.target.checked ? 'true' : null,
                                    })
                                }
                            />
                        }
                        label="Yes"
                    />
                </FormGroup>
            )}
        </div>
    )
}

interface MessageFilterSectionProps {
    hasMessagesFilter: boolean
    onHasMessagesFilterChange: (value: boolean) => void
}

export const MessageFilterSection: React.FC<MessageFilterSectionProps> = ({
    hasMessagesFilter,
    onHasMessagesFilterChange,
}) => {
    const theme = useTheme()

    const handleClearFilter = () => {
        onHasMessagesFilterChange(false)
    }

    return (
        <div
            style={{
                marginBottom: theme.spacing(3),
                padding: theme.spacing(2),
                backgroundColor: theme.palette.background.paper,
                borderRadius: theme.shape.borderRadius,
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: theme.spacing(2),
                }}
            >
                <Typography
                    variant="h6"
                    style={{
                        color: hasMessagesFilter
                            ? theme.palette.primary.main
                            : theme.palette.text.primary,
                        fontWeight: 500,
                    }}
                >
                    Messages
                </Typography>
                {hasMessagesFilter && (
                    <button
                        onClick={handleClearFilter}
                        style={{
                            border: 'none',
                            background: 'none',
                            color: theme.palette.primary.main,
                            cursor: 'pointer',
                            padding: theme.spacing(1),
                            fontSize: '0.875rem',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        Clear
                    </button>
                )}
            </div>

            <FormGroup>
                <FormControlLabel
                    control={
                        <Switch
                            checked={hasMessagesFilter}
                            onChange={(e) =>
                                onHasMessagesFilterChange(e.target.checked)
                            }
                            color="primary"
                        />
                    }
                    label="Has messages"
                />
            </FormGroup>
        </div>
    )
}
